import type { ActualUnitsListResponse } from "@classes-service-types/index.ts";
import { type UseQueryOptions, useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { computed, type Ref, unref } from "vue";
import { learningServiceRequest } from "@/services/utils";

interface Args<TData = ActualUnitsListResponse> {
  housingId: Ref<string> | string;
  unitId: Ref<string> | string;
  limit?: Ref<number> | number;
  skip?: Ref<number> | number;
  field?: Ref<string> | string;
  order?: Ref<"ASC" | "DESC"> | "ASC" | "DESC";
  q?: Ref<string> | string;
  useQueryOptions?: Partial<UseQueryOptions<ActualUnitsListResponse, Error, TData>>;
}

export const getQueryKey = (
  housingId: Ref<string> | string,
  unitId: Ref<string> | string,
  limit?: Ref<number> | number,
  skip?: Ref<number> | number,
  field?: Ref<string> | string,
  order?: Ref<"ASC" | "DESC"> | "ASC" | "DESC",
  q?: Ref<string> | string,
) => [
  "getActualUnits",
  unref(housingId),
  unref(unitId),
  unref(limit),
  unref(skip),
  unref(field),
  unref(order),
  unref(q),
];

export const useGetActualUnits = <TData = ActualUnitsListResponse>({
  housingId,
  unitId,
  limit,
  skip,
  field,
  order,
  q,
  useQueryOptions,
}: Args<TData>) => {
  const fetchActualUnits = async (): Promise<ActualUnitsListResponse> => {
    return learningServiceRequest({
      method: "get",
      url: `/housings/${unref(housingId)}/unit/${unref(unitId)}/actual_units/`,
      params: {
        limit: unref(limit) ?? 1000,
        skip: unref(skip) ?? 0,
        field: unref(field),
        order: unref(order),
        q: unref(q) ?? "",
      },
    }).then((response: AxiosResponse<ActualUnitsListResponse>) => response.data);
  };

  return useQuery<ActualUnitsListResponse, Error, TData>({
    queryKey: computed(() => getQueryKey(housingId, unitId, limit, skip, field, order, q)),
    queryFn: fetchActualUnits,
    ...useQueryOptions,
  });
};
